import { GetOptions, Report } from "@/core";
import {
  InventoryMonthlyReport,
  InventoryValueSnapshot,
  MonthlyReport,
  ProductReport,
  ReportType,
} from "@/core/entities/report";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getNextPageParam } from "../utils/page-params";

const databaseService = serviceHost.getDatabaseService();
const reportRepository = repositoryHost.getReportRepository(databaseService);
const functionsService = serviceHost.getFunctionsService();

// Coerce a snapshot `date` to a "YYYY-MM-DD" string. Handles strings already in
// that shape, full ISO strings, and Date objects (from Timestamp conversion).
function toIsoDateKey(value: unknown): string {
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return "";
}

export const useReports = () => {
  return useQuery({
    queryKey: ["reports", "get"],
    queryFn: async () => await reportRepository.getAll({}),
  });
};

export const useReportById = (reportId: string) => {
  return useQuery({
    queryKey: ["reports", "get", reportId],
    queryFn: async () => await reportRepository.get({ id: reportId }),
  });
};

export const useMonthlyReports = () => {
  // Backwards-compat retained for internal hooks that may still call without options
  // Defaults to paginated infinite query with sensible defaults
  return useMonthlyReportsPaginated({
    pagination: { limit: 20 },
    orderBy: { field: "dataAsOf", direction: "desc" },
  });
};

export const useMonthlyReportsPaginated = (options: GetOptions<MonthlyReport>) => {
  return useInfiniteQuery({
    queryKey: ["reports", "monthly", JSON.stringify(options)],
    queryFn: async ({ pageParam }) => {
      const reports = await reportRepository.getAll({
        ...options,
        queryConstraints: [
          { field: "type", operator: "==", value: ReportType.MONTHLY },
          ...(options.queryConstraints || []),
        ] as GetOptions<Report>["queryConstraints"],
        pagination: {
          ...(options.pagination || {}),
          cursor: pageParam,
        },
      });
      return reports as MonthlyReport[];
    },
    initialPageParam: "",
    getNextPageParam: getNextPageParam<MonthlyReport>(options),
  });
};

export const useProductReports = () => {
  // Backwards-compat retained for existing imports
  return useProductReportsPaginated({
    pagination: { limit: 20 },
    orderBy: { field: "dataAsOf", direction: "desc" },
  });
};

export const useProductReportsPaginated = (options: GetOptions<ProductReport>) => {
  return useInfiniteQuery({
    queryKey: ["reports", "product", JSON.stringify(options)],
    queryFn: async ({ pageParam }) => {
      const reports = await reportRepository.getAll({
        ...options,
        queryConstraints: [
          { field: "type", operator: "==", value: ReportType.PRODUCT },
          ...(options.queryConstraints || []),
        ] as GetOptions<Report>["queryConstraints"],
        pagination: {
          ...(options.pagination || {}),
          cursor: pageParam,
        },
      });
      return reports as ProductReport[];
    },
    initialPageParam: "",
    getNextPageParam: getNextPageParam<ProductReport>(options),
  });
};

export const useMonthlyReport = (year: number, month: number) => {
  const monthKey = `${year}-${month.toString().padStart(2, "0")}`;
  return useQuery({
    queryKey: ["reports", "monthly", monthKey],
    queryFn: async () => {
      const reports = await reportRepository.getAll({
        queryConstraints: [
          { field: "type", operator: "==", value: ReportType.MONTHLY },
          { field: "monthKey", operator: "==", value: monthKey },
        ] as GetOptions<Report>["queryConstraints"],
        pagination: {
          limit: 1,
        },
      });
      return reports[0] as MonthlyReport | undefined;
    },
    enabled: !!year && !!month && month >= 1 && month <= 12,
  });
};

export const useProductReport = (productId: string) => {
  return useQuery({
    queryKey: ["reports", "product", productId],
    queryFn: async () => {
      const reports = await reportRepository.getAll({
        queryConstraints: [
          { field: "type", operator: "==", value: ReportType.PRODUCT },
          { field: "productId", operator: "==", value: productId },
        ] as GetOptions<Report>["queryConstraints"],
        pagination: {
          limit: 1,
        },
      });
      return reports[0] as ProductReport | undefined;
    },
    enabled: !!productId,
  });
};

// All monthly inventory reports (one doc per month), newest month first.
export const useInventoryMonthlyReports = () => {
  return useQuery({
    queryKey: ["reports", "inventory-monthly"],
    queryFn: async () => {
      const reports = await reportRepository.getAll({
        queryConstraints: [
          {
            field: "type",
            operator: "==",
            value: ReportType.INVENTORY_MONTHLY,
          },
        ] as GetOptions<Report>["queryConstraints"],
        // Order by dataAsOf (reuses the existing `type + dataAsOf` composite
        // index); the page sorts by monthKey for display.
        orderBy: { field: "dataAsOf", direction: "desc" },
        pagination: { limit: 120 },
      });
      // Inventory reports are stored in the same `reports` collection but are
      // not part of the deal-report `Report` union, so cast through unknown.
      return reports as unknown as InventoryMonthlyReport[];
    },
  });
};

// Daily inventory value snapshots within [startDate, endDate] (inclusive),
// ordered oldest → newest. Requires the `type + date` composite index on
// `reports`. The newest doc in range is the latest known value.
export const useInventoryValueSnapshots = (range: {
  startDate: string; // "YYYY-MM-DD"
  endDate: string; // "YYYY-MM-DD"
}) => {
  return useQuery({
    queryKey: ["reports", "inventory-value", range.startDate, range.endDate],
    queryFn: async () => {
      const reports = await reportRepository.getAll({
        queryConstraints: [
          {
            field: "type",
            operator: "==",
            value: ReportType.INVENTORY_VALUE_DAILY,
          },
          { field: "date", operator: ">=", value: range.startDate },
          { field: "date", operator: "<=", value: range.endDate },
        ] as GetOptions<Report>["queryConstraints"],
        orderBy: { field: "date", direction: "asc" },
        pagination: { limit: 1000 },
      });
      // Stored in the shared `reports` collection but outside the deal-report
      // `Report` union, so cast through unknown.
      //
      // `date` is meant to be a "YYYY-MM-DD" string, but older docs (or the
      // generic Timestamp→Date conversion in the database service) can surface
      // it as a Date. Normalize so downstream parseISO/format calls never see a
      // non-string value.
      return (reports as unknown as InventoryValueSnapshot[]).map((snapshot) => ({
        ...snapshot,
        date: toIsoDateKey(snapshot.date),
      }));
    },
    enabled: !!range.startDate && !!range.endDate,
  });
};

// Purchase-order report (count + gross value by source × payment timing) for a
// date range. On-demand BigQuery callable; the page pivots the flat rows.
export const usePurchaseOrderReport = (range: {
  startDate: string; // "YYYY-MM-DD"
  endDate: string; // "YYYY-MM-DD"
}) => {
  return useQuery({
    queryKey: ["reports", "purchase-orders", range.startDate, range.endDate],
    queryFn: async () =>
      await functionsService.getPurchaseOrderReport({
        startDate: range.startDate,
        endDate: range.endDate,
      }),
    enabled: !!range.startDate && !!range.endDate,
  });
};

// Sales report (one row per sale, with margin, days-to-sell, and the collected-
// vs-outstanding split) for a date range. On-demand BigQuery callable; the page
// derives all totals/averages and the by-product/category/payment-stage
// roll-ups from the rows. Pass an empty range to disable (used for the
// period-over-period comparison query when no comparison window is set).
export const useSalesOrderReport = (range: {
  startDate: string; // "YYYY-MM-DD"
  endDate: string; // "YYYY-MM-DD"
}) => {
  return useQuery({
    queryKey: ["reports", "sales-orders", range.startDate, range.endDate],
    queryFn: async () =>
      await functionsService.getSalesOrderReport({
        startDate: range.startDate,
        endDate: range.endDate,
      }),
    enabled: !!range.startDate && !!range.endDate,
  });
};
